import { Archive, EllipsisVertical, Pencil } from "lucide-react";
import { FC, useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";

import { Button } from "~/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { EditCaseDialog } from "~/features/case/components/EditCaseDialog";
import { trackEvent } from "~/utils/analytics";
import { ArchiveCaseAlertDialog } from "./ArchiveCaseAlertDialog";
import { DeleteCaseAlertDialog } from "./DeleteCaseAlertDialog";

interface CaseActionsProps {
  caseId: string;
}

export const CaseActions: FC<CaseActionsProps> = ({ caseId }) => {
  const [openEditCaseDialog, setOpenEditCaseDialog] = useState(false);
  const [showDeleteAlertDialog, setShowDeleteAlertDialog] = useState(false);
  const [showArchiveAlertDialog, setShowArchiveAlertDialog] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  useEffect(() => {
    if (dropdownOpen) {
      trackEvent({ eventName: "app/cases/case_actions.dropdown/open" });
    }
  }, [dropdownOpen]);

  const dropdownItems = [
    {
      icon: Pencil,
      messageId: "edit",
      onClick: () => {
        trackEvent({ eventName: "app/case_actions_menu/edit.link/click" });
        setOpenEditCaseDialog(true);
        setDropdownOpen(false);
      },
    },
    {
      icon: Archive,
      messageId: "archive",
      defaultMessage: "Archive",
      onClick: () => {
        trackEvent({ eventName: "app/case_actions_menu/archive.link/click" });
        setShowArchiveAlertDialog(true);
        setDropdownOpen(false);
      },
    },
  ];

  return (
    <>
      <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon">
            <EllipsisVertical className="w-4 h-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-32" align="end">
          <DropdownMenuGroup>
            {dropdownItems.map((item) => (
              <div key={item.messageId}>
                <DropdownMenuItem
                  className="cursor-pointer"
                  onClick={item.onClick}
                >
                  <item.icon className="mr-2 h-4 w-4" />
                  <FormattedMessage
                    id={item.messageId}
                    defaultMessage={item.defaultMessage}
                  />
                </DropdownMenuItem>
              </div>
            ))}
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>
      {showArchiveAlertDialog && (
        <ArchiveCaseAlertDialog
          open={showArchiveAlertDialog}
          setOpen={setShowArchiveAlertDialog}
          projectId={caseId}
        />
      )}
      {showDeleteAlertDialog && (
        <DeleteCaseAlertDialog
          open={showDeleteAlertDialog}
          setOpen={setShowDeleteAlertDialog}
          projectId={caseId}
        />
      )}
      {openEditCaseDialog && (
        <EditCaseDialog
          open={openEditCaseDialog}
          setOpen={setOpenEditCaseDialog}
          projectId={caseId}
        />
      )}
    </>
  );
};
